import {
  CPUIcon,
  CodeIcon,
  FileIcon,
  FolderIcon,
  ImageIcon,
  OGCIcon,
  RanaAlertProps,
  RanaLoading,
  RasterIcon,
  ScenarioIcon,
  StudyAreaIcon,
  TextIcon,
  VectorIcon,
} from "@nens/ranalib";
import { capitalizeFirst } from "@nens/shared-utils";

import { RanaFileStatusIcon, descriptorToAlertStatus } from "../components/rana-file-status-icon";
import { FileDataTypeEnum, FileFeatureTypeEnum } from "../enums/file-data-type";
import { AnyFile } from "../types/any-file";

import { getFileFeatureType } from "./get-feature-type";

export const fileTypeMetadataMap: Record<
  FileDataTypeEnum | FileFeatureTypeEnum,
  { Icon: React.ComponentType; name?: string }
> = {
  [FileFeatureTypeEnum.StudyArea]: { Icon: StudyAreaIcon, name: "Study area" },
  [FileDataTypeEnum.Schematisation]: { Icon: CPUIcon, name: "Model schematisation" },
  [FileDataTypeEnum.Ogc]: { Icon: OGCIcon, name: "OGC 3D Tiles" },
  [FileDataTypeEnum.Code]: { Icon: CodeIcon },
  [FileDataTypeEnum.Image]: { Icon: ImageIcon },
  [FileDataTypeEnum.Raster]: { Icon: RasterIcon },
  [FileDataTypeEnum.Scenario]: { Icon: ScenarioIcon },
  [FileDataTypeEnum.Text]: { Icon: TextIcon },
  [FileDataTypeEnum.Vector]: { Icon: VectorIcon },
  [FileDataTypeEnum.Shapefile]: { Icon: RanaLoading },
};

const getErrorIcon = (fileOrDirectory: AnyFile) => {
  if (fileOrDirectory.type !== "file") return undefined;
  const status = descriptorToAlertStatus(fileOrDirectory.descriptor);
  if (status !== "error") return undefined;
  return (props: RanaAlertProps) => RanaFileStatusIcon({ file: fileOrDirectory, ...props });
};

const getMetadataForFile = (
  fileOrDirectory: AnyFile,
  layerInFile?: string
): { Icon: React.ComponentType; name?: string } => {
  const { type, dataType } = fileOrDirectory;

  if (type === "directory") return { Icon: FolderIcon };
  if (!dataType) return { Icon: RanaLoading, name: "" };

  const featureType = getFileFeatureType(fileOrDirectory);

  const meta =
    fileTypeMetadataMap[featureType as FileFeatureTypeEnum] ??
    fileTypeMetadataMap[dataType as FileDataTypeEnum];

  const ErrorIcon = getErrorIcon(fileOrDirectory);

  return {
    Icon: ErrorIcon ?? meta?.Icon ?? FileIcon,
    name: meta?.name ?? capitalizeFirst(dataType),
  };
};

export const getFileIcon = (fileOrDirectory: AnyFile, layerInFile?: string) => {
  return getMetadataForFile(fileOrDirectory, layerInFile).Icon;
};

export const getFileDisplayType = (fileOrDirectory: AnyFile, layerInFile?: string) => {
  return getMetadataForFile(fileOrDirectory, layerInFile).name;
};

export const getDisplayType = (
  dataOrFeatureType: FileFeatureTypeEnum | FileDataTypeEnum | string
) => {
  const meta = fileTypeMetadataMap[dataOrFeatureType as FileFeatureTypeEnum | FileDataTypeEnum];
  return meta?.name ?? capitalizeFirst(dataOrFeatureType) ?? "";
};
